import { apiRoutes } from "../../../_config/apiRoutes";
import { apiSlice } from "../../../_api/apiSlice";

export const upsellApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createUpsell: builder.mutation({
      query: (data) => ({
        url: apiRoutes.upsell.create.path,
        method: apiRoutes.upsell.create.reqType,
        body: data,
      }),
      invalidatesTags: ["Upsells"],
    }),
    resendUpsell: builder.mutation({
      query: (data) => ({
        url: apiRoutes.upsell.resend.path,
        method: apiRoutes.upsell.resend.reqType,
        body: data,
      }),
      invalidatesTags: ["Upsells"],
    }),
    editUpsell: builder.mutation({
      query: (data) => ({
        url: apiRoutes.upsell.edit.path,
        method: apiRoutes.upsell.edit.reqType,
        body: data,
      }),
      invalidatesTags: ["Upsells"],
    }),
    // Canonical backend route is GET /api/upsell/:id (also /upsell/upsell/:id legacy)
    getUpsell: builder.query({
      query: ({ id }) => ({
        url: `${apiRoutes.upsell.get.path}/${id}`,
        method: apiRoutes.upsell.get.reqType,
      }),
      providesTags: (result, error, { id }) => [{ type: "Upsells", id }],
    }),
    // Stat cards and tab counts, computed over every upsell on the server (MRR in USD).
    getUpsellStats: builder.query({
      query: () => ({ url: apiRoutes.upsell.stats.path, method: apiRoutes.upsell.stats.reqType }),
      providesTags: ["Upsells"],
    }),
    // What a new upsell starts from, per the org's Platform Settings ("On upsell").
    getUpsellDefaults: builder.query({
      query: () => ({ url: apiRoutes.upsell.defaults.path, method: apiRoutes.upsell.defaults.reqType }),
      providesTags: [{ type: "PlatformSettings", id: "SETTINGS" }],
    }),
    getAllUpsells: builder.query({
      query: (params) => ({
        url: apiRoutes.upsell.get.path,
        method: apiRoutes.upsell.get.reqType,
        params,
      }),
      providesTags: ["Upsells"],
    }),
    // Backs the "Pending approval" stat card - joined against
    // CustomerApproval server-side (see getUpsellsWithApprovalStatus),
    // not part of the plain getAllUpsells response. Note this endpoint's
    // own `page` param is 1-based (unlike every other list endpoint in
    // this app), so a caller passing `page` must account for that; a
    // stat-card count only needs the default first page at a large limit.
    getAllUpsellsWithApprovals: builder.query({
      query: (params) => ({
        url: apiRoutes.upsell.getAllWithApprovals.path,
        method: apiRoutes.upsell.getAllWithApprovals.reqType,
        params,
      }),
      providesTags: ["Upsells"],
    }),
    changeUpsellStatus: builder.mutation({
      query: (data) => ({
        url: apiRoutes.upsell.changeStatus.path,
        method: apiRoutes.upsell.changeStatus.reqType,
        body: data,
      }),
      invalidatesTags: ["Upsells"],
    }),
    approveUpsell: builder.mutation({
      query: ({ id }) => ({
        url: apiRoutes.upsell.approve.path.replace(":id", id),
        method: apiRoutes.upsell.approve.reqType,
      }),
      // Was missing entirely - a customer approving from the billing portal
      // never invalidated the "Upsells" tag other views read, so
      // CompanyUpsells/SubscriptionUpsells/UpsellDownsellPage could keep
      // showing a stale (pre-approval) status until an unrelated refetch.
      invalidatesTags: ["Upsells"],
    }),
    rejectUpsell: builder.mutation({
      query: ({ id }) => ({
        url: apiRoutes.upsell.reject.path.replace(":id", id),
        method: apiRoutes.upsell.reject.reqType,
      }),
      invalidatesTags: ["Upsells"],
    }),
    getApprovals: builder.query({
      query: (params) => ({
        url: apiRoutes.upsell.getApprovals.path,
        method: apiRoutes.upsell.getApprovals.reqType,
        params,
      }),
    }),
    getSubscriptionFormCompany: builder.mutation({
      query: (data) => ({
        url: apiRoutes.upsell.getSubscriptions.path,
        method: apiRoutes.upsell.getSubscriptions.reqType,
        body: data,
      }),
    }),
    // Live "what will actually get sent" render (see previewUpsellDraft in
    // upsellordownsell.controller.js) against the wizard's current, unsaved
    // draft state. Never invalidates/is invalidated by anything - it has no
    // side effects to cache against.
    previewUpsell: builder.mutation({
      query: (data) => ({
        url: apiRoutes.upsell.preview.path,
        method: apiRoutes.upsell.preview.reqType,
        body: data,
      }),
    }),
  }),
});

export const {
  useCreateUpsellMutation,
  useResendUpsellMutation,
  useEditUpsellMutation,
  useGetUpsellQuery,
  useGetAllUpsellsQuery,
  useGetAllUpsellsWithApprovalsQuery,
  useGetUpsellStatsQuery,
  useGetUpsellDefaultsQuery,
  useChangeUpsellStatusMutation,
  useApproveUpsellMutation,
  useRejectUpsellMutation,
  useGetApprovalsQuery,
  useGetSubscriptionFormCompanyMutation,
  usePreviewUpsellMutation,
} = upsellApiSlice;
